import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// 1. IMPORTUJEMY CONTEXT ZGÓD
import { useCookieConsent } from '../../contexts/CookieConsentContext';

import { useDeviceType } from '../../hooks/useDeviceType';
import appStoreBadge from '../../img/app-store-badge.svg';
import googlePlayBadge from '../../img/google-play-badge.svg';
import logo from '../../img/logo_full.png';
import appkaGif from '../../img/apka_gif.gif';

import NotebookBackground from '../../img/NotebookBackground';

import { FiShield, FiZap, FiTrendingUp, FiList } from 'react-icons/fi';

import { getFormattedPrices } from '../../utils/configUtils';

// --- KOMPONENTY POMOCNICZE ---

const PhoneMockup = ({ src, alt, className = "" }) => (
  <div className={`relative mx-auto border-gray-900 bg-gray-900 border-[8px] md:border-[10px] rounded-[2rem] shadow-2xl overflow-hidden max-w-[260px] md:max-w-[300px] ${className}`}>
    <div className="h-full w-full bg-white rounded-[1.5rem] overflow-hidden relative z-10">
      <img src={src} className="w-full h-full object-cover" alt={alt} />
    </div>
  </div>
);

const BenefitItem = ({ icon, title, children }) => (
  <div className="flex items-start gap-4">
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="mt-1 opacity-80">{children}</p>
    </div>
  </div>
);

const StepItem = ({ number, title, children }) => (
  <div className="flex flex-col items-center text-center">
    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#E09F2E] text-[#0a101f] font-bold text-2xl font-apotek mb-4">
      {number}
    </div>
    <h3 className="text-lg font-semibold font-apotek uppercase mb-2">{title}</h3>
    <p className="opacity-80">{children}</p>
  </div>
);

// --- GŁÓWNY KOMPONENT ---

const TikTokLandingV2 = () => {
  const deviceType = useDeviceType();
  const pixelLibRef = useRef(null);
  
  // 2. WYCIĄGAMY STAN ZGODY
  const { consentState } = useCookieConsent();
  
  const prices = getFormattedPrices();

  const IS_MOBILE_BUILD = process.env.REACT_APP_IS_MOBILE === 'true';

  // === Inicjalizacja Pixela ===
  useEffect(() => {
    if (IS_MOBILE_BUILD) return;
    if (!consentState.marketing) return;

    const pixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID;

    if (pixelId) {
      import('tiktok-pixel')
        .then((module) => {
          const TikTokPixel = module.default;
          pixelLibRef.current = TikTokPixel;
          TikTokPixel.init(pixelId, {}, { debug: false });
          TikTokPixel.pageView();
        })
        .catch((err) => console.error(err));
    }
  }, [IS_MOBILE_BUILD, consentState.marketing]);

  // === Komponent CtaButton ===
  const CtaButton = ({ theme = 'light' }) => {
    // Ujednolicony styl: font APOTEK (uppercase) dla spójności z nagłówkami
    const greenButtonClasses = "w-full text-center px-8 py-4 text-xl font-bold text-white bg-[#007D00] rounded-lg shadow-lg transition-transform hover:scale-105 active:scale-95 font-apotek uppercase tracking-wider flex items-center justify-center";
    
    // Drop shadow, żeby badge nie znikał na ciemnym tle
    const badgeLinkClasses = "inline-block text-center transition-transform hover:scale-105 active:scale-95 duration-200 drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)]";
    const badgeImageClasses = "w-full max-w-[220px] mx-auto";
    
    const isMobile = deviceType === 'iOS' || deviceType === 'Android';
    
    // Spójna paleta kolorów tekstów pomocniczych
    const supportTextClasses = isMobile
      ? "text-xs md:text-sm text-[#E09F2E] mt-3 font-bold font-apotek uppercase tracking-wider opacity-90"
      : theme === 'dark'
        ? "text-sm text-blue-100/60 mt-3 font-sans" // Zmiana: blue-100 pasuje lepiej do blue-950 niż szary
        : "text-sm text-[#233855]/80 mt-3 font-sans";

    const loginLinkClasses = theme === 'dark'
      ? "text-blue-100 hover:text-white"
      : "text-[#233855] hover:text-black";
        
    const supportTextContent = isMobile
      ? "3 dni dostępu za darmo. Bez opłat z góry. Bez haczyków." 
      : "Bez płatności z góry. Bez haczyków.";

    const APP_STORE_LINK = "https://apps.apple.com/pl/app/matematly-matura-z-matematyki/id6754283687?l=pl"; 
    const GOOGLE_PLAY_LINK = "https://play.google.com/store/apps/details?id=matematly.pl"; 

    const handleTikTokClickEvent = IS_MOBILE_BUILD 
    ? () => {} 
    : () => { 
        if (!pixelLibRef.current) return;
        try {
          pixelLibRef.current.track('ClickButton', {
            content_name: 'TikTok Landing CTA',
            device_type: deviceType,
          });
        } catch (e) {}
      };

    if (deviceType === 'iOS') {
      return (
        <a href={APP_STORE_LINK} className={badgeLinkClasses} target="_blank" rel="noopener noreferrer" onClick={handleTikTokClickEvent}>
          <img src={appStoreBadge} alt="Pobierz z App Store" className={badgeImageClasses} />
          <p className={supportTextClasses}>{supportTextContent}</p>
        </a>
      );
    }

    if (deviceType === 'Android') {
      return (
        <a href={GOOGLE_PLAY_LINK} className={badgeLinkClasses} target="_blank" rel="noopener noreferrer" onClick={handleTikTokClickEvent}>
          <img src={googlePlayBadge} alt="Pobierz z Google Play" className={badgeImageClasses} />
          <p className={supportTextClasses}>{supportTextContent}</p>
        </a>
      );
    }

    return (
      <>
        <Link to="/register" className={greenButtonClasses} onClick={handleTikTokClickEvent}>
          Zaczynajmy!
        </Link>
        <p className={supportTextClasses}>{supportTextContent}</p>
        <div className={`mt-2 text-sm ${loginLinkClasses} opacity-80 font-sans`}>
          Masz już konto?{' '}
          <Link to="/login" className="font-semibold underline underline-offset-2 hover:opacity-100 transition-opacity">
            Zaloguj się
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden font-sans">
      <NotebookBackground />

      <div className="relative z-10">

        {/* ========================================== */}
        {/* SEKCJA 1: HERO (ZOPTYMALIZOWANA)           */}
        {/* ========================================== */}
        <header className="bg-blue-950 pt-8 pb-0 lg:py-24 px-6 text-[#F0F0F0] overflow-hidden">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
            
            {/* LEWA STRONA: COPY + CTA */}
            <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left z-10">
              
              {/* LOGO - Zmniejszone dla lepszych proporcji */}
              <img src={logo} alt="Matematly Logo" className="h-12 lg:h-16 w-auto mb-8 opacity-95" />
              
              {/* NAGŁÓWEK - Font Apotek (Mocny) */}
              <h1 className="text-[2.5rem] leading-[0.95] md:text-5xl lg:text-7xl font-bold tracking-tight mb-3 font-apotek uppercase lg:leading-[0.9]">
                TYLKO ZADANIA <br/>
                <span className="text-[#E09F2E] drop-shadow-md">TYPU MATURALNEGO</span>
              </h1>
              
              {/* PODTYTUŁ - Font Apotek (Lekki) + Odsunięcie (mb-10) */}
              <p className="text-lg lg:text-2xl font-medium text-blue-100/80 mb-10 font-apotek uppercase tracking-wide">
                Nie marnujesz czasu na nic innego
              </p>

              
              {/* GRUPA CTA */}
              <div className="flex flex-col w-full max-w-xs mx-auto lg:mx-0">
                
                {/* UVP - Zmiana na font-sans (Inter/System) dla czytelności. 
                    Wyraźne oddzielenie wizualne od nagłówków. */}
                <p className="text-base text-white font-normal mb-3 leading-tight opacity-95 font-sans">
                   Dopasujemy dla Ciebie zadania i zadbamy o powtórki
                </p>

                <CtaButton theme="dark" />
              </div>

            </div>

            {/* PRAWA STRONA: GIF (PHONE MOCKUP) */}
            <div className="flex-1 w-full flex justify-center lg:justify-end relative mt-0 lg:mt-0">
               {/* Poświata */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-[#E09F2E]/20 blur-[80px] rounded-full -z-10"></div>
               
               {/* Mockup - lekkie uniesienie w górę (-mb-12) */}
               <div className="transform rotate-[-3deg] hover:rotate-0 transition-transform duration-500 -mb-12 lg:mb-0">
                  <PhoneMockup 
                    src={appkaGif} 
                    alt="Aplikacja Matematly w akcji" 
                    className="shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)] border-gray-800 ring-1 ring-white/10" 
                  />
               </div>
            </div>

          </div>
        </header>

        {/* ========================================== */}
        {/* RESZTA SEKCJI BEZ ZMIAN (Twoje oryginały)  */}
        {/* ========================================== */}

        {/* ----- SEKCJA 2: (BENEFITS - WERSJA KONKRET) ----- */}
       {/* ----- SEKCJA 2: (BENEFITS - 3 FILARY) ----- */}
        <section className="bg-transparent pt-20 pb-16 px-6 text-[#233855]">
          <div className="max-w-6xl mx-auto">
            
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-apotek uppercase leading-none text-blue-950">
              INTELIGENTNA NAUKA <br/>
              <span className="text-[#E09F2E]">ZERO STRATY CZASU</span>
            </h2>

            {/* Zmiana na 3 kolumny (md:grid-cols-3) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* KAFELEK 1 - ALGORYTM + POWTÓRKI */}
              

              {/* KAFELEK 2 - MATERIAŁ CKE */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col items-center text-center h-full">
                  <div className="p-4 bg-blue-50 rounded-full text-[#E09F2E] mb-6">
                    <FiZap size={32} />
                  </div>
                  <h3 className="text-xl font-bold font-apotek uppercase leading-tight mb-3 text-blue-950">
                    100% Matury
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-sans">
                    Rozwiązujesz wyłącznie zadania <span className="font-semibold text-[#233855]">typu maturalnego</span>. Masz integrację z <span className="font-semibold text-[#233855]">Kartą Wzorów</span>. Skupiasz się na maturze.
                  </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col items-center text-center h-full">
                  <div className="p-4 bg-blue-50 rounded-full text-[#E09F2E] mb-6">
                    <FiShield size={32} />
                  </div>
                  <h3 className="text-xl font-bold font-apotek uppercase leading-tight mb-3 text-blue-950">
                    Zadania dla Ciebie
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-sans">
                    Algorytm na bieżąco analizuje Twoje postępy i dobiera <span className="font-semibold text-[#233855]">zadania dla Ciebie</span>. Dba też o <span className="font-semibold text-[#233855]">powtórki</span>, żeby wiedza została w głowie aż do matury.
                  </p>
              </div>

              {/* KAFELEK 3 - FEEDBACK */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col items-center text-center h-full">
                  <div className="p-4 bg-blue-50 rounded-full text-[#E09F2E] mb-6">
                    <FiTrendingUp size={32} />
                  </div>
                  <h3 className="text-xl font-bold font-apotek uppercase leading-tight mb-3 text-blue-950">
                    Błąd? Spoko, naprawimy to
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-sans">
                    Od razu dostajesz <span className="font-semibold text-[#233855]">rozwiązanie krok po kroku</span> i odblokowujesz <span className="font-semibold text-[#233855]">zadania naprawcze</span>. Zamieniasz każdą pomyłkę w konkretną wiedzę.
                  </p>
              </div>

            </div>
          </div>
        </section>
        {/* ----- SEKCJA 3: (CENNIK + KROKI) ----- */}
        <section className="bg-blue-950 py-20 px-6 text-[#F0F0F0]">
          
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-3xl md:text-5xl font-bold font-apotek uppercase mb-6 tracking-wide">
              UCZCIWY UKŁAD<br/> <span className="text-[#E09F2E]">ZERO RYZYKA</span>
            </h2>
            <p className="text-lg text-blue-100/80 max-w-xl mx-auto">
              Nie kupujesz kota w worku. Najpierw używasz, potem decydujesz.
            </p>
          </div>

          {/* KROKI - TO JEST NAJWAŻNIEJSZE (Sprzedajemy proces instalacji) */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-24 relative">
            {/* Ozdobna linia w tle */}
            <div className="hidden md:block absolute top-6 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white/20 to-transparent -z-10"></div>

            <StepItem number="1" title="Pobierasz">
              Dostajesz <b>3 dni dostępu za darmo</b> na start. Nie podajesz karty. Nie ma żadnych haczyków.
            </StepItem>
            <StepItem number="2" title="Testujesz">
              Sprawdzasz, czy Matematly Ci pasuje.
            </StepItem>
            <StepItem number="3" title="Decydujesz">
              Po 3 dniach apka zapyta, co dalej. Możesz wykupić dostęp lub po prostu ją usunąć. <b>Ty rządzisz.</b>
            </StepItem>
          </div>

          {/* CENNIK - TYLKO INFORMACYJNIE (Subtelny, spłaszczony) */}
          <div className="max-w-4xl mx-auto text-center border-t border-white/10 pt-8">
            <h3 className="text-xl md:text-2xl font-bold font-apotek uppercase mb-4 text-blue-100/90">
              Ile to kosztuje PO okresie próbnym?
            </h3>
            <p className="text-base text-blue-100/60 mb-8 max-w-2xl mx-auto">
              Mniej niż jedna godzina korepetycji. Poważnie.
            </p>

            {/* Karty - minimalizm, bez przycisków, bez wyróżnień */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 opacity-90 hover:opacity-100 transition-opacity duration-300">
              

              {/* Box 2 - Minimalnie inny, ale nie agresywny */}
              {/* <div className="bg-[#0a101f]/50 border border-white/20 rounded-lg p-5 relative overflow-hidden">
                <h4 className="font-bold font-apotek uppercase text-[#E09F2E] mb-1 text-lg">Do Matury</h4>
                <div className="flex justify-center items-baseline gap-1">
                    <span className="text-2xl font-bold text-white font-apotek">{prices.plan_do_matury}</span>
                </div>
                <p className="text-xs text-blue-100/40 mt-1 uppercase tracking-wider">Płacisz raz</p>
              </div> */}
              {/* Box 1 */}
              <div className="bg-[#0a101f]/30 border border-white/10 rounded-lg p-5">
                <h4 className="font-bold font-apotek uppercase text-blue-200 mb-1 text-lg">Sprint</h4>
                <div className="flex justify-center items-baseline gap-1">
                    <span className="text-2xl font-bold text-white font-apotek">{prices.plan_1m}</span>
                </div>
                <p className="text-xs text-blue-100/40 mt-1 uppercase tracking-wider">1 miesiąc</p>
              </div>

              

              {/* Box 3 */}
              <div className="bg-[#0a101f]/30 border border-white/10 rounded-lg p-5">
                <h4 className="font-bold font-apotek uppercase text-blue-200 mb-1 text-lg">Maraton</h4>
                <div className="flex justify-center items-baseline gap-1">
                    <span className="text-2xl font-bold text-white font-apotek">{prices.plan_3m}</span>
                </div>
                <p className="text-xs text-blue-100/40 mt-1 uppercase tracking-wider">3 miesiące</p>
              </div>

            </div>
            
            <p className="text-[10px] text-blue-100/20 mt-8 font-sans">
              *To nie jest subskrypcja. Płacisz tylko za wybrany okres. Żadnego pobierania kasy z konta bez Twojej wiedzy.
            </p>

          </div>
        </section>

        {/* ----- SEKCJA 4: FINISH (BEZ DUBLOWANIA GIF-A) ----- */}
        <section className="bg-transparent pt-24 pb-32 px-6 text-center text-[#233855]">
          
          <div className="max-w-3xl mx-auto">
            
            {/* Nagłówek: Konkretna obietnica. Duży i głośny. */}
            <h2 className="text-4xl md:text-6xl font-bold mb-10 text-balance font-apotek uppercase text-blue-950 leading-[0.9]">
              TYLKO ZADANIA typu maturalnego  <br/>
              <span className="text-[#E09F2E]">DOBRANE DLA CIEBIE</span>
            </h2>

            {/* CTA Container - Bez zbędnego gadania */}
            <div className="flex flex-col gap-4 max-w-xs mx-auto relative z-10">
              <CtaButton />
              
              <Link to="/login" className="text-gray-500 text-sm font-semibold hover:text-[#233855] mt-4 transition-colors">
                Mam już konto, chcę się zalogować
              </Link>
            </div>

            {/* Subtelny gradient zamykający stronę */}
            <div className="absolute left-0 bottom-0 w-full h-48 bg-gradient-to-t from-blue-50/50 to-transparent -z-10 pointer-events-none"></div>
          </div>

        </section>

        {/* ----- Stopka ----- */}
        <footer className="bg-blue-950 text-center py-6 px-6 text-xs text-[#F0F0F0]/50">
          © {new Date().getFullYear()} Matematly. Wszelkie prawa zastrzeżone.
        </footer>

      </div>
    </div>
  );
};

export default TikTokLandingV2;